/**
 * Running sim shared by the sprints, the hurdles run and the distance events. Pure and
 * fixed-step: feed it presses stamped in sim time and call `advance` to a time; the
 * client and the server produce identical states from identical inputs.
 */
import {
  createCadence,
  recordPress,
  updateCadence,
  type Cadence,
  type PressResult,
  type RunKey,
} from '../input.js';
import { clamp } from '../rng.js';

/** Fixed simulation step (s). */
export const SIM_DT = 1 / 50;

export interface StaminaConfig {
  /** Cadence you can hold indefinitely (presses/s). */
  sustainable: number;
  /** Fraction of the bar drained per second when running one full sustainable-cadence over the limit. */
  drainPerSec: number;
  /** Fraction refilled per second when under the limit. */
  refillPerSec: number;
  /** Top speed multiplier when the bar is empty. */
  emptyFactor: number;
}

export interface RunnerConfig {
  /** Race distance (m). */
  distance: number;
  hurdles?: { strict: boolean };
  /** Speed at the sweet-spot cadence (m/s). */
  topSpeed: number;
  /** Presses per second that give full speed. */
  sweetCadence: number;
  /** How quickly speed closes on its target (1/s). */
  accel: number;
  /** How quickly speed falls when the target drops (1/s). */
  decel: number;
  stamina: StaminaConfig | null;
  /** Presses earlier than this many seconds before the gun are ignored; later ones are a false start. */
  falseStartWindow: number;
}

export const SPRINT_100M: RunnerConfig = {
  distance: 100,
  topSpeed: 10.6,
  sweetCadence: 10,
  accel: 1.6,
  decel: 2.5,
  stamina: null,
  falseStartWindow: 1.0,
};

export interface RunnerState {
  /** Sim time since the gun (s); negative during the start sequence. */
  t: number;
  jumpAt: number | null;
  jumpY: number;
  hurdle: number;
  hits: number;
  disqualified: boolean;
  x: number;
  v: number;
  cadence: Cadence;
  /** 0..1 */
  stamina: number;
  started: boolean;
  falseStart: boolean;
  finished: boolean;
  /** Finishing time, interpolated inside the final step. */
  time: number | null;
  /** Presses not yet consumed by `advance`, sorted by time. */
  pending: { key: RunKey; t: number }[];
}

export function createRunner(startAt = -3): RunnerState {
  return {
    t: startAt,
    jumpAt: null,
    jumpY: 0,
    hurdle: 0,
    hits: 0,
    disqualified: false,
    x: 0,
    v: 0,
    cadence: createCadence(),
    stamina: 1,
    started: false,
    falseStart: false,
    finished: false,
    time: null,
    pending: [],
  };
}

export type RunnerPressResult = PressResult | 'false-start' | 'too-early' | 'finished' | 'penalty';

/** Cadence up to this multiple of the sustainable rhythm does not drain stamina. */
export const STAMINA_GRACE = 1.08;

/** A false start costs this long standing still after the gun. */
export const FALSE_START_PENALTY = 0.6;

/**
 * Queue a press. Presses are applied in time order during `advance`, so the result here
 * only reports gating (finished, before the gun); alternation is judged when applied.
 */
export function runnerPress(
  s: RunnerState,
  cfg: RunnerConfig,
  key: RunKey,
  t: number,
): RunnerPressResult {
  if (s.finished) return 'finished';
  if (t < 0) {
    if (t < -cfg.falseStartWindow) return 'too-early';
    s.falseStart = true;
    return 'false-start';
  }
  if (s.falseStart && t < FALSE_START_PENALTY) return 'penalty';
  // Presses may arrive slightly out of order across a network; keep the queue sorted.
  const p = { key, t };
  let i = s.pending.length;
  while (i > 0 && s.pending[i - 1]!.t > t) i--;
  s.pending.splice(i, 0, p);
  return 'counted';
}

/** Speed multiplier for cadence relative to the sweet spot: saturates just above 1. */
export const SPEED_CAP = 1.04;
export function speedCurve(ratio: number): number {
  if (ratio <= 0) return 0;
  return Math.min(SPEED_CAP, ratio ** 0.9);
}

/** Step the sim in fixed increments up to `until` seconds. */
export function runnerAdvance(s: RunnerState, cfg: RunnerConfig, until: number): void {
  while (!s.finished && s.t + SIM_DT <= until + 1e-9) {
    const next = s.t + SIM_DT;
    // Apply presses that fall inside this step.
    while (s.pending.length && s.pending[0]!.t <= next) {
      const p = s.pending.shift()!;
      if (recordPress(s.cadence, p.key, p.t) === 'counted') s.started = true;
    }
    stepRunner(s, cfg, next);
  }
}

function stepRunner(s: RunnerState, cfg: RunnerConfig, next: number): void {
  const dt = SIM_DT;
  s.t = next;
  if (s.t < 0) return; // start sequence: nothing moves
  const cadence = updateCadence(s.cadence, s.t, dt);
  let target = cfg.topSpeed * speedCurve(cadence / cfg.sweetCadence);

  if (cfg.stamina) {
    const st = cfg.stamina;
    // A grace band above the sustainable rhythm: the cadence meter's fast attack and slow
    // release read a steady rhythm slightly high, which used to bleed the bar dry.
    const limit = st.sustainable * STAMINA_GRACE;
    if (cadence > limit) {
      s.stamina -= st.drainPerSec * ((cadence - limit) / st.sustainable) * dt;
    } else {
      s.stamina += st.refillPerSec * dt;
    }
    s.stamina = clamp(s.stamina, 0, 1);
    if (s.stamina <= 0) target *= st.emptyFactor;
    else if (s.stamina < 0.2) target *= st.emptyFactor + (1 - st.emptyFactor) * (s.stamina / 0.2);
  }

  const k = target > s.v ? cfg.accel : cfg.decel;
  const v0 = s.v;
  s.v += (target - s.v) * (1 - Math.exp(-dt * k));
  const x0 = s.x;
  s.x += ((v0 + s.v) / 2) * dt;

  if (cfg.hurdles) {
    const air = s.jumpAt === null ? -1 : s.t - s.jumpAt;
    s.jumpY = air >= 0 ? Math.max(0, 5.4 * air - 4.905 * air * air) : 0;
    if (air > 1.11) s.jumpAt = null;
    const hurdleX = 13.72 + s.hurdle * 9.14;
    if (s.hurdle < 10 && s.x >= hurdleX) {
      if (s.jumpY < 1.067) {
        s.hits++;
        s.v *= 0.55;
      }
      s.hurdle++;
      if (cfg.hurdles.strict && s.hits >= 3) {
        s.disqualified = true;
        s.finished = true;
        return;
      }
    }
  }
  if (s.x >= cfg.distance) {
    const frac = (cfg.distance - x0) / Math.max(1e-9, s.x - x0);
    s.time = Math.round((s.t - dt + frac * dt) * 100) / 100;
    s.x = cfg.distance;
    s.finished = true;
  }
}

/** Speed in m/s as a fraction of the configured top speed, for HUDs and audio. */
export function speedFraction(s: RunnerState, cfg: RunnerConfig): number {
  return clamp(s.v / cfg.topSpeed, 0, 1.1);
}

export function runnerAction(s: RunnerState, cfg: RunnerConfig, down: boolean, t: number): void {
  if (!cfg.hurdles || !down || s.finished || t < 0) return;
  runnerAdvance(s, cfg, t);
  if (s.jumpAt === null) s.jumpAt = s.t;
}
